import React, { useState, useEffect } from 'react';
import createAxiosInstance from '../../utils/axiosConfig';
import { 
  get_questions_by_flow_id, 
  create_question, 
  update_question, 
  delete_question 
} from '../../utils/endpoints';
import Alert from './alerts/alert';
import bgPublic from '../../assets/img/bg-public.png';
import grayBgTransparent from '../../assets/img/gray-bg-transparent.png';
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface EditQuestionsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  questionFlowData: any | null;
  onSuccess: () => void;
}

interface QuestionFormData {
  flow_id: number;
  question_text: string;
  order: number;
  is_enabled: boolean;
  is_deleted: boolean;
}

interface Question {
  id: number;
  flow_id: number;
  question_text: string;
  order: number;
  is_enabled: boolean;
  is_deleted: boolean;
}

// Sortable Question Item Component
const SortableQuestionItem: React.FC<{
  question: Question;
  index: number;
  onEdit: (question: Question) => void;
  onDelete: (questionId: number) => void;
  isEditing: boolean;
}> = ({ question, index, onEdit, onDelete, isEditing }) => {
  // Ensure we have a valid ID
  const itemId = question.id || `question-${question.flow_id}-${index}`;
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: itemId,
    data: {
      type: 'question',
      question,
      index
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    display: 'grid',
    gridTemplateColumns: '50px 80px 1fr 150px 60px',
    padding: '12px 8px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '0.875rem',
    fontFamily: 'Roboto',
    alignItems: 'center',
    backgroundColor: isDragging ? '#f8f9fa' : 'white',
    borderRadius: isDragging ? '8px' : '0',
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {/* Drag handle */}
      <div 
        {...listeners}
        style={{ 
          textAlign: 'center',
          cursor: 'grab',
          padding: '8px',
          borderRadius: '4px',
          backgroundColor: 'transparent',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = '#e3f2fd';
            e.currentTarget.style.cursor = 'grab';
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        <i 
          className="bi bi-grip-vertical" 
          style={{ 
            color: isDragging ? '#183FCE' : '#6c757d', 
            fontSize: '16px',
            transition: 'color 0.2s ease',
            pointerEvents: 'none'
          }}
          title="Drag to reorder"
        />
      </div>
      
      {/* Order */}
      <div>
        <span className="badge bg-secondary">
          {question.order || index + 1}
        </span>
      </div>
      
      {/* Question text */}
      <div style={{ 
        overflow: 'hidden', 
        textOverflow: 'ellipsis', 
        whiteSpace: 'nowrap',
        paddingRight: '10px'
      }}>
        {question.question_text || 'Unknown Question'}
      </div>
      
      {/* Status */}
      <div>
        <span className={`badge ${question.is_enabled ? 'bg-success' : 'bg-secondary'}`}>
          {question.is_enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>
      
      {/* Actions */}
      <div style={{ textAlign: 'center' }}>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary me-1"
          onClick={() => onEdit(question)}
          disabled={isEditing}
          title="Edit question"
        >
          <i className="bi bi-pencil"></i>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-danger"
          onClick={() => onDelete(question.id)}
          disabled={isEditing}
          title="Delete question"
        >
          <i className="bi bi-trash"></i>
        </button>
      </div>
    </div>
  );
};

const EditQuestionsSidebar: React.FC<EditQuestionsSidebarProps> = ({
  isOpen,
  onClose,
  questionFlowData,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'danger' | 'warning'; show: boolean } | null>(null);
  
  // Form state for adding/editing questions
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [questionFormData, setQuestionFormData] = useState<QuestionFormData>({
    flow_id: 0,
    question_text: '',
    order: 1,
    is_enabled: true,
    is_deleted: false
  });
  const [questionErrors, setQuestionErrors] = useState<{ [key: string]: string }>({});

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch questions when component opens
  useEffect(() => {
    if (isOpen && questionFlowData?.id) {
      fetchQuestions();
    }
  }, [isOpen, questionFlowData]);

  const fetchQuestions = async () => {
    if (!questionFlowData?.id) return;
    
    setLoading(true);
    try {
      const response = await createAxiosInstance().get(
        get_questions_by_flow_id(questionFlowData.id)
      );
      
      if (response.status === 200) {
        // Sort questions by order
        const sortedQuestions = (response.data || []).sort((a: Question, b: Question) => a.order - b.order);
        setQuestions(sortedQuestions);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setAlert({
        message: 'Error fetching questions. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Form validation
  const validateQuestionForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!questionFormData.question_text.trim()) {
      newErrors.question_text = 'Question text is required';
    }
    setQuestionErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form changes
  const handleQuestionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setQuestionFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'order' ? parseInt(value) || 1 : value)
    }));
    
    if (questionErrors[name]) {
      setQuestionErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Add new question
  const handleAddQuestion = async () => {
    if (!validateQuestionForm()) return;
    
    setLoading(true);
    try {
      const response = await createAxiosInstance().post(create_question, questionFormData);
      
      if (response.status === 200 || response.status === 201) {
        const newQuestion = {
          ...questionFormData,
          ...response.data,
          id: response.data.id || response.data?.data?.id || Date.now() // Fallback ID
        };
        
        setQuestions(prev => [...prev, newQuestion].sort((a, b) => a.order - b.order));
        
        // Reset form
        const nextOrder = Math.max(...questions.map(q => q.order), 0) + 1;
        setQuestionFormData({
          flow_id: questionFlowData.id,
          question_text: '',
          order: nextOrder,
          is_enabled: true,
          is_deleted: false
        });
        
        setAlert({
          message: 'Question added successfully!',
          type: 'success',
          show: true
        });
        setIsAddingQuestion(false);
      }
    } catch (error) {
      console.error('Error adding question:', error);
      setAlert({
        message: 'Error adding question. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Update existing question
  const handleUpdateQuestion = async () => {
    if (!validateQuestionForm() || !editingQuestion?.id) return;
    
    setLoading(true);
    try {
      const updatedQuestionData = {
        flow_id: questionFormData.flow_id || editingQuestion.flow_id || questionFlowData?.id,
        question_text: questionFormData.question_text,
        order: questionFormData.order,
        is_enabled: questionFormData.is_enabled,
        is_deleted: questionFormData.is_deleted
      };
      
      const response = await createAxiosInstance().put(
        update_question(editingQuestion.id), 
        updatedQuestionData
      );
      
      if (response.status === 200 || response.status === 201) {
        setQuestions(prev => 
          prev.map(q => 
            q.id === editingQuestion.id 
              ? { ...q, ...updatedQuestionData }
              : q
          ).sort((a, b) => a.order - b.order)
        );
        
        setAlert({
          message: 'Question updated successfully!',
          type: 'success',
          show: true
        });
        setEditingQuestion(null);
        resetQuestionForm();
      }
    } catch (error) {
      console.error('Error updating question:', error);
      setAlert({
        message: 'Error updating question. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete question
  const handleDeleteQuestion = async (questionId: number) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    setLoading(true);
    try {
      const response = await createAxiosInstance().delete(delete_question(questionId));
      
      if (response.status === 200 || response.status === 204) {
        setQuestions(prev => prev.filter(q => q.id !== questionId));
        setAlert({
          message: 'Question deleted successfully!',
          type: 'success',
          show: true
        });
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      setAlert({
        message: 'Error deleting question. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle drag end event for reordering
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    console.log('🔴 Drag ended:', { activeId: active.id, overId: over?.id });

    if (!over || active.id === over.id) {
      console.log('❌ No valid drop target or same position');
      return; // No change needed
    }

    // Find indices using both real ID and fallback ID
    const oldIndex = questions.findIndex((question, idx) => 
      question.id === active.id || `question-${question.flow_id}-${idx}` === active.id
    );
    const newIndex = questions.findIndex((question, idx) => 
      question.id === over.id || `question-${question.flow_id}-${idx}` === over.id
    );

    console.log('📍 Found indices:', { oldIndex, newIndex });

    if (oldIndex === -1 || newIndex === -1) {
      console.error('❌ Invalid indices found');
      return; // Invalid indices
    }

    console.log('🎯 Drag & Drop:', { 
      from: oldIndex, 
      to: newIndex, 
      question: questions[oldIndex].question_text?.substring(0, 30) + '...' 
    });

    // Reorder the questions array
    const reorderedQuestions = arrayMove(questions, oldIndex, newIndex);
    
    // Update order values and make API calls
    const updatePromises = reorderedQuestions.map(async (question, index) => {
      const newOrder = index + 1;
      if (question.order !== newOrder) {
        // Ensure we have a valid question ID
        const questionId = question.id;
        
        if (!questionId) {
          console.error('❌ No valid question ID found for update:', question);
          return question;
        }

        const updatedQuestionData = {
          flow_id: question.flow_id || questionFlowData?.id,
          question_text: question.question_text,
          order: newOrder,
          is_enabled: question.is_enabled ?? true,
          is_deleted: question.is_deleted ?? false
        };

        console.log('📤 Updating question:', { questionId, newOrder, oldOrder: question.order, updatedData: updatedQuestionData });
        
        try {
          const response = await createAxiosInstance().put(update_question(questionId), updatedQuestionData);
          console.log('✅ Question update successful:', response.data);
          return { ...question, order: newOrder };
        } catch (error: any) {
          console.error('❌ Error updating question order:', error);
          setAlert({
            message: `Error updating question order: ${error.response?.data?.detail || error.message}`,
            type: 'danger',
            show: true
          });
          return question;
        }
      }
      return { ...question, order: newOrder };
    });

    // Update local state immediately for better UX
    setQuestions(reorderedQuestions.map((q, index) => ({ ...q, order: index + 1 })));

    try {
      const results = await Promise.all(updatePromises);
      console.log('✅ All question order updates completed');
      setQuestions(results);
      
      // Show success alert for successful reordering
      setAlert({
        message: 'Questions reordered successfully!',
        type: 'success',
        show: true
      });
    } catch (error) {
      console.error('❌ Error in batch question reordering:', error);
      setAlert({
        message: 'Error reordering questions. Please refresh and try again.',
        type: 'danger',
        show: true
      });
      // Refresh questions to get the correct state from server
      fetchQuestions();
    }
  };

  // Edit question
  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    setQuestionFormData({
      flow_id: question.flow_id,
      question_text: question.question_text,
      order: question.order,
      is_enabled: question.is_enabled,
      is_deleted: question.is_deleted
    });
    setIsAddingQuestion(true); // Use the same form
  };

  // Start adding new question
  const startAddingQuestion = () => {
    setEditingQuestion(null);
    const nextOrder = Math.max(...questions.map(q => q.order), 0) + 1;
    setQuestionFormData({
      flow_id: questionFlowData?.id || 0,
      question_text: '',
      order: nextOrder,
      is_enabled: true,
      is_deleted: false
    });
    setIsAddingQuestion(true);
  };

  // Reset question form
  const resetQuestionForm = () => {
    setQuestionFormData({
      flow_id: questionFlowData?.id || 0,
      question_text: '',
      order: 1,
      is_enabled: true,
      is_deleted: false
    });
    setQuestionErrors({});
    setIsAddingQuestion(false);
    setEditingQuestion(null);
  };

  const handleClose = () => {
    resetQuestionForm();
    setAlert(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="offcanvas-backdrop fade show"
        onClick={handleClose}
      ></div>
      <div
        className="offcanvas offcanvas-end show"
        tabIndex={-1}
        style={{ 
          zIndex: 1055, 
          width: '60vw',
          maxWidth: '800px'
        }}
      >
        <div className="offcanvas-header" style={{backgroundColor: 'white', borderBottom: '1px solid #dee2e6'}}>
          <h5 className="offcanvas-title" style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: '#183FCE'
          }}>
            Edit Questions - {questionFlowData?.title || questionFlowData?.name}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={handleClose}
          ></button>
        </div>
        
        <div className="offcanvas-body">
          {loading && !isAddingQuestion ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Add Question Button */}
              <div className="mb-4">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={startAddingQuestion}
                  disabled={isAddingQuestion || loading}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  Add New Question
                </button>
              </div>

              {/* Add/Edit Question Form */}
              {isAddingQuestion && (
                <div className="card mb-4">
                  <div className="card-header">
                    <h6 className="mb-0">
                      {editingQuestion ? 'Edit Question' : 'Add New Question'}
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label htmlFor="questionText" className="form-label">Question Text:</label>
                      <textarea
                        className={`form-control bg-white ${questionErrors.question_text ? 'is-invalid' : ''}`}
                        id="questionText"
                        name="question_text"
                        rows={3}
                        value={questionFormData.question_text}
                        onChange={handleQuestionChange}
                        disabled={loading}
                      />
                      {questionErrors.question_text && (
                        <div className="invalid-feedback">{questionErrors.question_text}</div>
                      )}
                    </div>
                    
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label htmlFor="questionOrder" className="form-label">Order:</label>
                          <input
                            type="number"
                            className="form-control bg-white"
                            id="questionOrder"
                            name="order"
                            min="1"
                            value={questionFormData.order}
                            onChange={handleQuestionChange}
                            disabled={loading}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <div className="form-check mt-4">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="questionEnabled"
                              name="is_enabled"
                              checked={questionFormData.is_enabled}
                              onChange={handleQuestionChange}
                              disabled={loading}
                            />
                            <label className="form-check-label" htmlFor="questionEnabled">
                              Enable Question
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-light"
                        onClick={resetQuestionForm}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-rounded"
                        onClick={editingQuestion ? handleUpdateQuestion : handleAddQuestion}
                        disabled={loading}
                      >
                        {loading ? (
                          <div className="d-flex align-items-center">
                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                            {editingQuestion ? 'Updating...' : 'Adding...'}
                          </div>
                        ) : (
                          editingQuestion ? 'Update Question' : 'Add Question'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Questions List */}
              <div>
                <h6 className="mb-3">Questions ({questions.length})</h6>
                {questions.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="bi bi-question-circle display-4 mb-3"></i>
                    <p>No questions found for this question flow.</p>
                    <p>Click "Add New Question" to get started.</p>
                  </div>
                ) : (
                  <div>
                    <div className="mb-2">
                      <small className="text-muted">
                        <i className="bi bi-info-circle me-1"></i>
                        Drag questions by the grip handle (⋮⋮) to reorder them. Changes are saved automatically.
                      </small>
                    </div>
                    
                    {/* Flow Questions Section - Matching Framework Setup UI */}
                    <div style={{ 
                      marginBottom: '24px', 
                      border: '1px solid #dee2e6', 
                      borderRadius: '8px', 
                      backgroundColor: 'white',
                      overflow: 'hidden'
                    }}>
                      {/* Flow Header */}
                      <div style={{
                        position: 'relative',
                        color: 'white',
                        padding: '12px 16px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        overflow: 'hidden'
                      }}>
                        {/* Background Image Layer */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundImage: `url(${bgPublic})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat',
                          zIndex: 1
                        }}></div>
                        
                        {/* Gray Overlay Layer */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundImage: `url(${grayBgTransparent})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'right',
                          backgroundRepeat: 'no-repeat',
                          zIndex: 2
                        }}></div>
                        
                        {/* Content Layer */}
                        <span style={{ position: 'relative', zIndex: 3 }}>
                          {questionFlowData?.title || questionFlowData?.name || 'Question Flow'}
                        </span>
                        <span className="badge bg-light text-dark" style={{ position: 'relative', zIndex: 3 }}>
                          {questions.length} question{questions.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* Questions Header */}
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '50px 80px 1fr 150px 60px',
                        backgroundColor: '#f8f9fa', 
                        borderBottom: '1px solid #dee2e6',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#183FCE',
                        padding: '12px 8px'
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <i className="bi bi-arrows-move" title="Drag to reorder"></i>
                        </div>
                        <div>Order</div>
                        <div>Question Text</div>
                        <div>Status</div>
                        <div style={{ textAlign: 'center' }}>Actions</div>
                      </div>

                      {/* Drag and Drop Context for this flow */}
                      <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                        <DndContext 
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragStart={(event) => {
                            console.log('🟢 Drag started:', event.active.id);
                          }}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext 
                            items={questions.map((q, idx) => q.id || `question-${q.flow_id}-${idx}`)}
                            strategy={verticalListSortingStrategy}
                          >
                            {questions.map((question, index) => (
                              <SortableQuestionItem
                                key={question.id || `question-${question.flow_id}-${index}`}
                                question={question}
                                index={index}
                                onEdit={handleEditQuestion}
                                onDelete={handleDeleteQuestion}
                                isEditing={isAddingQuestion}
                              />
                            ))}
                          </SortableContext>
                        </DndContext>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Alert */}
      {alert && (
        <Alert
          message={alert.message}
          type={alert.type}
          show={alert.show}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default EditQuestionsSidebar; 